import { ConfluenceIcon, JiraIcon } from '@atlaskit/logo';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { messages } from '@databases/intl/messages';
import React, { type FunctionComponent, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { AiSummarizeMenuItem, FieldTypeMenuItem } from './AddFieldMenuItem';
import { AddFieldMenuSearchInput } from './AddFieldMenuSearchInput';
import { AdvancedFieldTypeSection } from './AdvancedFieldTypeSection';
import { addFieldMenuMessages } from './messages';
import type { AddFieldMenuProps } from './types';
import { useFieldTypeEntries } from './useFieldTypeEntries';
import { includesSearch } from './utils';

export type { AddFieldMenuProps, AddFieldMenuSelection } from './types';

const menuStyles = xcss({
  width: '344px',
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 32px)',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'elevation.surface.overlay',
  borderRadius: 'radius.medium',
  boxShadow: 'elevation.shadow.overlay',
  borderColor: 'color.border',
  borderStyle: 'solid',
  borderWidth: 'border.width',
  overflow: 'hidden'
});

const menuContentStyles = xcss({
  overflowY: 'auto'
});

const searchWrapperStyles = xcss({
  paddingBlockStart: 'space.100',
  paddingInline: 'space.150',
  paddingBlockEnd: 'space.075'
});

const sectionStyles = xcss({
  paddingBlock: 'space.075',
  paddingInline: 'space.150'
});

const separatedSectionStyles = xcss({
  borderTopColor: 'color.border',
  borderTopStyle: 'solid',
  borderTopWidth: 'border.width'
});

const sectionTitleStyles = xcss({
  paddingBlockStart: 'space.075',
  paddingBlockEnd: 'space.075',
  paddingInline: 'space.050'
});

const itemGridStyles = xcss({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  columnGap: 'space.100',
  rowGap: 'space.025'
});

const noResultsStyles = xcss({
  paddingInline: 'space.100',
  paddingBlock: 'space.200'
});

export const AddFieldMenu: FunctionComponent<AddFieldMenuProps> = ({
  onSelect,
  showTypes
}) => {
  const { formatMessage } = useIntl();
  const [searchValue, setSearchValue] = useState('');
  const { basic, confluence, jira, basicLabel, confluenceLabel, jiraLabel } =
    useFieldTypeEntries(showTypes);

  const aiSummaryLabel = formatMessage(addFieldMenuMessages.aiSummary);
  const shouldShowAiSummarize = includesSearch(aiSummaryLabel, searchValue);
  const filteredBasic = basic.filter(({ label }) => includesSearch(label, searchValue));
  const filteredConfluence = confluence.filter(({ label }) =>
    includesSearch(label, searchValue)
  );
  const filteredJira = jira.filter(({ label }) => includesSearch(label, searchValue));
  const hasResults =
    shouldShowAiSummarize ||
    filteredBasic.length > 0 ||
    filteredConfluence.length > 0 ||
    filteredJira.length > 0;
  const isSearching = searchValue.trim().length > 0;
  const searchPlaceholder = formatMessage(addFieldMenuMessages.searchPlaceholder);

  return (
    <Box xcss={menuStyles} testId="add-field-menu">
      <Box xcss={searchWrapperStyles}>
        <AddFieldMenuSearchInput
          testId="add-field-menu-search-input"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={setSearchValue}
          ariaLabel={searchPlaceholder}
        />
      </Box>

      <Box xcss={menuContentStyles}>
        {shouldShowAiSummarize && (
          <Box xcss={sectionStyles}>
            <Box xcss={sectionTitleStyles}>
              <Text size="small" weight="semibold" color="color.text.subtle">
                {formatMessage(addFieldMenuMessages.aiFieldsSection)}
              </Text>
            </Box>
            <Box xcss={itemGridStyles}>
              <AiSummarizeMenuItem onSelect={onSelect} />
            </Box>
          </Box>
        )}

        {filteredBasic.length > 0 && (
          <Box xcss={[sectionStyles, separatedSectionStyles]}>
            <Box xcss={sectionTitleStyles}>
              <Text size="small" weight="semibold" color="color.text.subtle">
                {basicLabel}
              </Text>
            </Box>
            <Box xcss={itemGridStyles}>
              {filteredBasic.map((fieldType) => (
                <FieldTypeMenuItem
                  key={fieldType.type}
                  fieldType={fieldType}
                  onSelect={onSelect}
                />
              ))}
            </Box>
          </Box>
        )}

        {(filteredConfluence.length > 0 || filteredJira.length > 0) && (
          <Box xcss={[sectionStyles, separatedSectionStyles]}>
            <Box xcss={sectionTitleStyles}>
              <Text size="small" weight="semibold" color="color.text.subtle">
                {formatMessage(addFieldMenuMessages.advancedFieldTypesSection)}
              </Text>
            </Box>
            <Stack space="space.025">
              {filteredConfluence.length > 0 && (
                <AdvancedFieldTypeSection
                  label={confluenceLabel}
                  Icon={ConfluenceIcon}
                  items={filteredConfluence}
                  isExpandedBySearch={isSearching}
                  onSelect={onSelect}
                />
              )}
              {filteredJira.length > 0 && (
                <AdvancedFieldTypeSection
                  label={jiraLabel}
                  Icon={JiraIcon}
                  items={filteredJira}
                  isExpandedBySearch={isSearching}
                  onSelect={onSelect}
                />
              )}
            </Stack>
          </Box>
        )}

        {!hasResults && (
          <Box xcss={noResultsStyles}>
            <Text color="color.text.subtlest">
              {formatMessage(
                messages['field-settings.field-type-dropdown-no-matches-text']
              )}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AddFieldMenu;
